"use client"

import { Head, router, usePage } from "@inertiajs/react"
import { ArrowLeft, Check } from "@untitledui/icons"
import { useState } from "react"

import { batyrGroupPhotoBlob as fallbackImage } from "@/assets/images/photos"
import { SplitBackgroundEffect, SplitImageWrapper } from "@/components/auth"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Link } from "@/components/ui/link"
import { BatyrLogo, BatyrLogoVariant } from "@/components/ui/logo-batyr"
import { AuthSplitLayout } from "@/layouts/auth/auth-split-layout"
import { cn } from "@/lib/utils"
import { identityPasswordResetPath, signInPath } from "@/routes"

// ----------------------------------------------------------------------------
// Form Pattern: Uncontrolled Inputs + FormData (with a twist)
// ----------------------------------------------------------------------------
// See sessions/new.tsx for the general pattern explanation.
//
// This page shows live password requirement indicators (8+ chars, special
// character) that need the current password value. We keep a local `password`
// state purely for that validation UI — there is no `value` prop on the Input,
// so it stays uncontrolled and FormData still collects it on submit.
// ----------------------------------------------------------------------------

interface PageProps {
  errors: Record<string, string>
  [key: string]: unknown
}

interface ResetPasswordProps {
  email: string
  sid: string
}

export default function ResetPassword({ sid }: ResetPasswordProps) {
  const { errors } = usePage<PageProps>().props
  const [processing, setProcessing] = useState(false)

  // Track password for validation UI only - not for form submission
  const [password, setPassword] = useState("")

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    router.put(
      identityPasswordResetPath({ sid }),
      Object.fromEntries(new FormData(e.currentTarget)),
      {
        onStart: () => setProcessing(true),
        onFinish: () => setProcessing(false),
      },
    )
  }

  return (
    <AuthSplitLayout sidebar={<SplitImageWrapper src={fallbackImage} />}>
      <SplitBackgroundEffect />
      <Head title="Set new password" />
      <header className="flex flex-col items-center gap-6 text-center">
        <BatyrLogo variant={BatyrLogoVariant.ICON} className="h-12 w-auto" />
        <div className="flex flex-col gap-2 md:gap-3">
          <h1 className="text-display-sm text-primary font-semibold text-balance">
            Set new password
          </h1>
          <p className="text-md text-tertiary">
            Your new password must be different to previously used passwords.
          </p>
        </div>
      </header>

      <form onSubmit={submit} className="flex flex-col gap-6">
        <div className="flex flex-col gap-5">
          {/*
            onChange here is only for validation UI - no value prop means
            this is still uncontrolled. FormData handles actual submission.
          */}
          <Input
            label="Password"
            type="password"
            name="password"
            placeholder="••••••••"
            size="md"
            isRequired
            hideRequiredIndicator
            onChange={setPassword}
            isInvalid={!!errors?.password}
            errorMessage={errors?.password}
            autoFocus
            autoComplete="new-password"
          />
          <Input
            label="Confirm password"
            type="password"
            name="password_confirmation"
            placeholder="••••••••"
            size="md"
            isRequired
            hideRequiredIndicator
            isInvalid={!!errors?.password_confirmation}
            errorMessage={errors?.password_confirmation}
            autoComplete="new-password"
          />

          {/* Password requirements - driven by local state */}
          <div className="flex flex-col gap-3">
            <RequirementRow
              met={password.length >= 8}
              label="Must be at least 8 characters"
            />
            <RequirementRow
              met={/[!@#$%^&*(),.?":{}|<>]/.test(password)}
              label="Must contain one special character"
            />
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <Button
            type="submit"
            size="lg"
            isDisabled={processing}
            isLoading={processing}
            showTextWhileLoading
          >
            Reset password
          </Button>
        </div>
      </form>

      <div className="flex justify-center gap-1 text-center">
        <Link
          href={signInPath()}
          className="text-tertiary hover:text-secondary flex items-center gap-2 text-sm font-semibold"
        >
          <ArrowLeft className="size-4" />
          Back to log in
        </Link>
      </div>
    </AuthSplitLayout>
  )
}

// A single live password-requirement indicator: a check that fills in once the
// rule is met. Quiet (grey) until satisfied, then success-solid.
function RequirementRow({ met, label }: { met: boolean; label: string }) {
  return (
    <span className="flex items-center gap-2">
      <span
        className={cn(
          "flex size-5 items-center justify-center rounded-full transition duration-150 ease-in-out",
          met
            ? "bg-success-solid text-white"
            : "bg-quaternary text-fg-quaternary",
        )}
      >
        <Check aria-hidden="true" className="size-3" />
      </span>
      <p className="text-tertiary text-sm">{label}</p>
    </span>
  )
}
